import { useEffect, useState } from "react";
import { CalendarRange, CalendarDays, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

// Which update this notice describes. Announcing the next one means changing
// this, the rows below and the updateNotice.* strings in extraStrings.js:
// every account has seen the old identifier and none has seen the new one.
const UPDATE_ID = "2026-09-planning";

// One row per change, each in the icon and colour its section already wears,
// so the notice points at something the reader will find on the page:
// Planning's tile on the home page, the calendar's own icon in WeekCalendar,
// and the shield of the key rotation page. A row without a body shows its
// title alone.
const ITEMS = [
  {
    id: "planning",
    icon: CalendarRange,
    tint: "bg-[#E63946]",
    title: "updateNotice.planning",
    body: "updateNotice.planningBody",
  },
  {
    id: "calendar",
    icon: CalendarDays,
    tint: "bg-indigo-500",
    title: "updateNotice.calendar",
    body: "updateNotice.calendarBody",
  },
  {
    id: "security",
    icon: ShieldCheck,
    tint: "bg-[#0F766E]",
    title: "updateNotice.security",
  },
];

// Per account as well as per update: a browser shared by two people shows the
// notice to each of them.
function storageKey(userId) {
  return `core.updateNotice.${userId}.${UPDATE_ID}`;
}

// Whether the notice should open for this account.
//
// Everything that touches localStorage is inside the try. Blocked site data,
// the private mode of older Safari releases or a full quota make it throw or
// refuse the write, and then there is nowhere to remember that the notice was
// seen: it would open at every visit, which is worse than not opening at all.
// So storage that cannot be written means no notice.
function isUnseen(userId) {
  try {
    const store = window.localStorage;
    const probe = "core.storageProbe";
    store.setItem(probe, "1");
    store.removeItem(probe);
    return store.getItem(storageKey(userId)) === null;
  } catch {
    return false;
  }
}

function markSeen(userId) {
  try {
    window.localStorage.setItem(storageKey(userId), "1");
  } catch {
    // The probe in isUnseen passed a moment ago. If this write fails anyway,
    // the notice opens again at the next visit, which is the lesser failure.
  }
}

/**
 * What changed in Core, shown once to each account after an update.
 *
 * Mounted in the layout, which only renders for a signed-in user, so it opens
 * on whichever page somebody lands on after signing in. Frontend only: the
 * record of having seen it lives in the browser, and a second device shows it
 * once more.
 */
export default function UpdateNotice() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const userId = user?._id;
  const [open, setOpen] = useState(false);

  // Recorded as seen the moment it opens rather than when it is closed, so it
  // appears once even if the tab is closed with the notice still on screen.
  //
  // The effect only ever opens it. StrictMode runs effects twice in
  // development, and the second run finds the key already written: an effect
  // that also closed the notice would close it straight away.
  useEffect(() => {
    if (userId && isUnseen(userId)) {
      markSeen(userId);
      setOpen(true);
    }
  }, [userId]);

  if (!userId) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* No description: the title and the rows are the whole content.
          Declaring that there is none keeps Radix from warning about it. */}
      <DialogContent className="sm:max-w-md" aria-describedby={undefined} data-testid="update-notice">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{t("updateNotice.title")}</DialogTitle>
        </DialogHeader>
        <ul className="space-y-4 mt-1">
          {ITEMS.map((item) => (
            <li key={item.id} className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl ${item.tint} flex items-center justify-center shrink-0`}>
                <item.icon className="w-5 h-5 text-white" />
              </div>
              <div className={item.body ? "min-w-0 flex-1" : "min-w-0 flex-1 self-center"}>
                <p className="text-sm font-semibold text-slate-900">{t(item.title)}</p>
                {item.body && <p className="text-sm text-slate-600 mt-0.5">{t(item.body)}</p>}
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap justify-end gap-3 pt-1">
          <Button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="update-notice-ok"
            className="bg-indigo-700 hover:bg-indigo-600 text-white"
          >
            {t("updateNotice.ok")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
